<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type Job, jobsApi } from '@/api/entities/admin/jobs'

definePage({
  meta: { title: 'Job 큐 모니터링', action: 'read', subject: 'review' },
})

const { handleError } = useErrorHandler()
const loading = ref(false)
const jobs = ref<Job[]>([])
const total = ref(0)
const currentPage = ref(1)
const lastPage = ref(1)
const queueFilter = ref('')

const headers = [
  { title: 'ID', key: 'id', sortable: false },
  { title: 'Queue', key: 'queue', sortable: false },
  { title: 'Config ID', key: 'config_id', sortable: false },
  { title: 'Attempts', key: 'attempts', sortable: false },
  { title: 'Available At', key: 'available_at', sortable: false },
]

const loadJobs = async () => {
  loading.value = true
  try {
    const { success, data } = await jobsApi.list(queueFilter.value || undefined, currentPage.value)
    if (success && data) {
      jobs.value = data.data
      total.value = data.total
      lastPage.value = data.last_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

const onPageChange = (page: number) => {
  currentPage.value = page
  loadJobs()
}

const formatTimestamp = (ts: number | null): string => {
  if (!ts) return '-'
  
  return new Date(ts * 1000).toLocaleString('ko-KR')
}

onMounted(loadJobs)
</script>

<template>
  <VCard>
    <VCardTitle class="pa-4">
      Job 큐 모니터링
    </VCardTitle>
    <VCardText>
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="4"
        >
          <VTextField
            v-model="queueFilter"
            label="Queue 필터"
            clearable
            hide-details
            @keyup.enter="loadJobs"
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <VBtn
            color="primary"
            @click="loadJobs"
          >
            검색
          </VBtn>
        </VCol>
      </VRow>
      <VDataTable
        :headers="headers"
        :items="jobs"
        :loading="loading"
        hide-default-footer
      >
        <template #item.available_at="{ item }">
          {{ formatTimestamp(item.available_at) }}
        </template>
        <template #no-data>
          <div class="text-center py-8">
            대기 중인 Job이 없습니다.
          </div>
        </template>
      </VDataTable>
      <div
        v-if="lastPage > 1"
        class="d-flex justify-center mt-4"
      >
        <VPagination
          :model-value="currentPage"
          :length="lastPage"
          @update:model-value="onPageChange"
        />
      </div>
    </VCardText>
  </VCard>
</template>
